import {
	ErrorModal,
	useContextMenuMESF,
	useShiftNavigator,
} from "@dexteel/mesf-core";
import { Button, Grid2, Paper } from "@mui/material";
import {
	createPart,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { themeDXT } from "@/setup/ag-grid-license";
import { routeAdjustmentManager } from "@/setup/routes/adjustment.routes";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTableData } from "../../hooks/hooks";
import { useAdjustmentOptionFunctions } from "../../hooks/useAdjustmentOptionFunctions";
import { MillAdjustmentByShift } from "../../models/MillAdjustmentByShift";
import { deleteAdjustment } from "../../repositories/AdjustmentManagerRepository";
import { UpsertAdjustmentModal } from "../UpsertAdjustmentModal";

const headersPart = createPart({
	feature: "headersTable",
	css: `
    .start-column {
      border-left: 2px solid #ccc !important
    }`,
});
export const customThemeWithHeaders = themeDXT.withPart(headersPart);

type Props = {
	adjustmentId: string | null;
	isLoading: boolean;
	rows: MillAdjustmentByShift[];
};

export const AdjustmentsManagerTable = ({
	adjustmentId,
	isLoading,
	rows,
}: Props) => {
	const navigate = useNavigate();
	const { shiftInfo } = useShiftNavigator();

	const [isDeleteLoading, setIsDeleteLoading] = useState(false);
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [selectedId, setSelectedId] = useState<number | null>(null);
	const [showModal, setShowModal] = useState<"new" | "edit" | "delete" | "">(
		"",
	);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [focusComments, setFocusComments] = useState(false);

	const handleNewAdjustment = () => {
		if (gridApi) gridApi.deselectAll();
		setShowModal("new");
		setSelectedId(null);
		navigate(
			`${routeAdjustmentManager}/${shiftInfo?.CurrentShiftId}?adjustmentId=null`,
		);
	};

	const {
		editData,
		deleteData,
		messageDeleteModal,
		setMessageDeleteModal,
		getMenuOptions,
	} = useAdjustmentOptionFunctions({
		setSelectedId,
		setShowModal,
		handleNewAdjustment,
	});

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { columnDefs } = useTableData({
		editData,
		showContextMenu,
		deleteData,
	});
	const { defaultColDef } = useGridDefinitions({});

	const queryClient = useQueryClient();
	const deleteMutation = useMutation(deleteAdjustment, {
		onSuccess: async () => {
			await queryClient.invalidateQueries(["adjustments"]);
			setNotificationMessage("Adjustment deleted successfully");
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsDeleteLoading(false),
	});

	const onRowClicked = (event: any) => {
		const cell = event.api?.getFocusedCell();
		if (cell) {
			if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
				if (cell?.column.getColId() === "Comments") {
					setFocusComments(true);
				} else {
					setFocusComments(false);
				}
				setSelectedId((event.data as MillAdjustmentByShift).MillAdjustmentId);
				setShowModal("edit");
				navigate(
					`${routeAdjustmentManager}/${
						shiftInfo?.CurrentShiftId
					}?adjustmentId=${
						(event.data as MillAdjustmentByShift).MillAdjustmentId
					}`,
				);
			}
		}
	};

	const handleDeleteAdjustment = async (value?: string) => {
		setShowModal("");
		setMessageDeleteModal("");
		navigate(`${routeAdjustmentManager}/${shiftInfo?.CurrentShiftId}`);
		if (value !== "OK") return;
		if (selectedId === null) return;
		setIsDeleteLoading(true);
		deleteMutation.mutate(selectedId);
	};

	const getContextMenuItems = (
		params: GetContextMenuItemsParams,
	): (DefaultMenuItem | MenuItemDef)[] => {
		const data = params.node?.data;
		showContextMenu(event as any, data, "adjustmentManagerContext");
		if (data) {
			params.api.deselectAll();
			params.node?.setSelected(true);
		}
		return [];
	};

	const onModalHide = () => {
		setShowModal("");
		setSelectedId(null);
		if (gridApi) gridApi.deselectAll();
		navigate(`${routeAdjustmentManager}/${shiftInfo?.CurrentShiftId}`);
	};

	useEffect(() => {
		if (!isLoading && !isDeleteLoading) {
			if (adjustmentId === "null") {
				handleNewAdjustment();
			} else {
				if (adjustmentId) {
					const adjustmentNode = gridApi?.getRowNode(adjustmentId);

					if (adjustmentNode) {
						gridApi?.ensureNodeVisible(adjustmentNode);

						adjustmentNode.setSelected(true);
						setSelectedId(
							(adjustmentNode.data as MillAdjustmentByShift).MillAdjustmentId,
						);
						setShowModal("edit");
					} else {
						navigate(`${routeAdjustmentManager}/${shiftInfo?.CurrentShiftId}`);
					}
				}
			}
		}
	}, [isLoading, isDeleteLoading, gridApi]);

	useEffect(() => {
		registerConfig({
			id: "adjustmentManagerContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<div onContextMenu={(e) => e.preventDefault()}>
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				sx={{
					height: "70vh",
					minHeight: "70vh",
				}}
				component={Paper}
				elevation={2}
				spacing={1}
			>
				<Grid2 size={{ md: 12 }} sx={{ height: "100%" }}>
					<AgGridReact<MillAdjustmentByShift>
						loading={isLoading}
						onGridReady={(params) => setGridApi(params.api)}
						pagination={false}
						animateRows
						rowHeight={38}
						headerHeight={35}
						singleClickEdit
						tooltipShowDelay={0}
						tooltipHideDelay={2000}
						rowData={rows}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						getContextMenuItems={getContextMenuItems}
						onRowClicked={onRowClicked}
						getRowId={(params) => String(params.data.MillAdjustmentId)}
						loadingOverlayComponent={CenteredLazyLoading}
						gridOptions={{
							rowSelection: {
								mode: "singleRow",
								checkboxes: false,
								enableClickSelection: true,
							},
							theme: customThemeWithHeaders,
						}}
					/>
				</Grid2>
				<Grid2 container size={{ md: 12 }} justifyContent="flex-end">
					<Grid2 size={{ md: 2 }}>
						<Button
							variant="contained"
							color="primary"
							fullWidth
							onClick={() => {
								handleNewAdjustment();
							}}
						>
							NEW ADJUSTMENT
						</Button>
					</Grid2>
				</Grid2>
			</Grid2>
			<UpsertAdjustmentModal
				showModal={showModal}
				onHide={onModalHide}
				millAdjustmentId={selectedId}
				focusComments={focusComments}
				suffixTitle="Adjustments Manager"
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
			<ConfirmationDialogRaw
				title="Confirm Delete"
				message={messageDeleteModal}
				open={showModal === "delete"}
				onClose={handleDeleteAdjustment}
				suffixTitle="Adjustments Manager"
			/>
		</div>
	);
};
